import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/lib/i18n/useLocale';
import { cn } from '@/lib/ui/cn';
import { authApi } from '@/features/auth/api';
import { useApiError } from '@/lib/query/useApiError';
import { AccountMenu } from '@/features/auth/AccountMenu';
import { GlobalSearch } from '@/features/search/GlobalSearch';
import { SidebarNav } from '@/features/website/SidebarNav';
import { IconClose, IconMenu } from '@/features/pageEditor/icons';
import { CapabilitiesBanner } from './CapabilitiesBanner';
import { WorkspaceContext } from './workspace';

/** Returns focus to whatever a ref points at when called — read at call time, not capture. */
function focusRef(ref: RefObject<HTMLElement>, fallback: HTMLElement | null): void {
  (ref.current ?? fallback)?.focus();
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Two-tier Almanac shell (plan §2.5.2): Ink-Navy sidebar (the gravitational center) +
 * a thin top utility strip. RTL-native via logical properties. The sidebar leads with the
 * website itself, page by page (SidebarNav); everything else renders from config + RBAC.
 *
 * The sidebar becomes a drawer below 768 px (`md`). A 900 px window — a laptop with a
 * browser beside an editor — keeps its sidebar. The drawer is a real one: a scrim that
 * dismisses, Escape, a focus trap while open, focus returned to the toggle on close, the
 * page behind it locked from scrolling, and a slide-in from the inline start edge (the
 * right edge in Arabic).
 *
 * A workspace screen (`useWideWorkspace`, the page editor) takes the full width: no reading
 * cap on the content, and the sidebar stays a drawer up to 1536 px (`2xl`), opened from a
 * labelled Menu button.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [wide, setWide] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  // A route change closes the drawer even when navigation did not come from a nav link
  // (a redirect after a save, the browser's Back button).
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const focusables = () =>
      Array.from(drawerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);

    // Deferred a frame: the drawer transitions `visibility` with `transform`, and at
    // progress 0 it is still `hidden`, where `.focus()` is a silent no-op.
    let raf = requestAnimationFrame(() => {
      const first = focusables()[0];
      first?.focus();
      if (first && document.activeElement !== first)
        raf = requestAnimationFrame(() => first.focus());
    });

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMobileOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;
      // Trap: the drawer covers the page, so Tab must not walk into the content behind it.
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      // The toggle mounted when the drawer CLOSES is the one to return focus to.
      focusRef(toggleRef, previouslyFocused);
    };
  }, [mobileOpen]);

  return (
    <WorkspaceContext.Provider value={setWide}>
      <div className="flex min-h-screen bg-canvas-sand">
        {/* Scrim: only below the sidebar breakpoint and only while open. `aria-hidden`
          because Escape and the close button are the accessible routes out. */}
        {mobileOpen && (
          <div
            aria-hidden
            data-testid="drawer-scrim"
            onClick={() => setMobileOpen(false)}
            className={cn('fixed inset-0 z-30 bg-ink-navy/40', wide ? '2xl:hidden' : 'md:hidden')}
          />
        )}

        {/* Sidebar */}
        <aside
          ref={drawerRef}
          id="app-sidebar"
          className={cn(
            'fixed inset-y-0 start-0 z-40 flex w-[280px] max-w-[85vw] flex-col bg-ink-navy text-sky-blue',
            'transition-[transform,visibility] duration-almanac ease-almanac',
            wide
              ? '2xl:sticky 2xl:top-0 2xl:h-screen 2xl:w-[248px] 2xl:max-w-none 2xl:visible 2xl:translate-x-0 2xl:rtl:translate-x-0'
              : 'md:sticky md:top-0 md:h-screen md:w-[248px] md:max-w-none md:visible md:translate-x-0 md:rtl:translate-x-0',
            // `invisible` takes a closed drawer out of the tab order and the a11y tree
            // together; `md:visible` gives the permanent sidebar straight back.
            mobileOpen ? 'translate-x-0' : 'invisible -translate-x-full rtl:translate-x-full',
          )}
        >
          <div className="flex items-center justify-between gap-sm border-b border-white/10 px-lg py-md">
            {/* The website's logo for dark backgrounds (its footer's), so the console
              wears the same mark. The image says "TEPCO"; the label says which tool. */}
            <div className="flex min-w-0 items-center gap-sm">
              <img
                src="/brand/sig-corp-white.svg"
                alt="TEPCO"
                width={116}
                height={32}
                className="h-[32px] w-auto shrink-0"
              />
              <span className="border-s border-white/20 ps-sm font-mono text-label-sm uppercase tracking-[0.12em] text-sky-blue">
                {t('app.consoleLabel')}
              </span>
            </div>
            {/* The drawer's own dismiss; hidden once the sidebar is permanent. */}
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label={t('ui.close')}
              className={cn(
                'inline-flex h-[44px] w-[44px] items-center justify-center rounded-sm text-sky-blue transition-colors duration-almanac hover:text-canvas-paper focus-visible:shadow-focus',
                wide ? '2xl:hidden' : 'md:hidden',
              )}
            >
              <IconClose />
            </button>
          </div>
          {/* `relative` makes the scroller the containing block of absolutely placed
            descendants (the `sr-only` text in an unseen-count badge). Without it their
            containing block is the sticky aside, so the nav cannot clip them: one sitting
            below the fold stretches the page and scrolls into empty space under the shell. */}
          <nav
            className="relative flex-1 overflow-y-auto px-sm py-md"
            aria-label={t('app.primaryNav')}
          >
            <SidebarNav onNavigate={() => setMobileOpen(false)} />
          </nav>
          {/* Which build is running, on every route: the first question in any
            support exchange is "which version are you on?". `__APP_VERSION__` is
            the repo-root VERSION file, inlined at build time, so it cannot go
            stale. A screen reader hears the sentence ("Version 1.0.0"); the eye
            sees the compact `v1.0.0`, isolated LTR so Arabic never reorders it. */}
          <div className="flex items-baseline justify-between gap-sm border-t border-white/10 px-lg py-sm">
            <p className="font-mono text-label-sm uppercase tracking-[0.12em] text-sky-blue/70">
              {t('app.edition')}
            </p>
            <p className="shrink-0" title={t('app.version', { version: __APP_VERSION__ })}>
              <span className="sr-only">{t('app.version', { version: __APP_VERSION__ })}</span>
              <bdi
                dir="ltr"
                aria-hidden="true"
                data-testid="app-version"
                className="font-mono text-label-sm tracking-[0.06em] text-sky-blue/70"
              >
                v{__APP_VERSION__}
              </bdi>
            </p>
          </div>
        </aside>

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar
            toggleRef={toggleRef}
            mobileOpen={mobileOpen}
            wide={wide}
            onToggleMobile={() => setMobileOpen((v) => !v)}
          />
          {/* `p-md` below 768 px: at 360 px, 24 px gutters spend too much of the viewport. */}
          <main
            id="main"
            className={cn(
              'mx-auto w-full flex-1 p-md md:p-lg lg:p-xl',
              wide ? 'max-w-[1920px]' : 'max-w-[1280px]',
            )}
          >
            <CapabilitiesBanner />
            {children}
          </main>
        </div>
      </div>
    </WorkspaceContext.Provider>
  );
}

function TopBar({
  onToggleMobile,
  mobileOpen,
  wide,
  toggleRef,
}: {
  onToggleMobile: () => void;
  mobileOpen: boolean;
  wide: boolean;
  toggleRef: RefObject<HTMLButtonElement>;
}) {
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();
  const onError = useApiError();
  // Publishes the bar's height as `--topbar-h`, so a record's sticky header (DetailShell)
  // parks just under it instead of sliding beneath it. Measured, because the bar is taller
  // below 768 px (44 px touch targets) and wraps when a long name fills it.
  const barRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--topbar-h', `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--topbar-h');
    };
  }, []);
  // Both halves of what an editor means by the toggle: this device remembers it at once
  // (localStorage, inside setLocale), and the profile records it so it follows them to
  // another machine — the same field the account page saves. A failed save is surfaced,
  // never swallowed; the local switch stands either way.
  const chooseLocale = (next: 'en' | 'ar') => {
    setLocale(next);
    authApi.updateMe({ locale: next }).catch(onError);
  };
  return (
    <header
      ref={barRef}
      className="sticky top-0 z-20 flex items-center gap-sm border-b-[0.5px] border-hairline bg-canvas-paper px-md py-sm md:gap-md md:px-lg"
    >
      <button
        ref={toggleRef}
        type="button"
        // 44 px square on touch widths; on a workspace screen at desktop widths it also says
        // what it is, because the sidebar it replaces was in plain sight a moment ago.
        className={cn(
          'inline-flex h-[44px] min-w-[44px] items-center justify-center gap-xs rounded-sm text-ink-deep transition-colors duration-almanac hover:bg-canvas-sand focus-visible:shadow-focus',
          wide ? 'md:border-[0.5px] md:border-hairline-strong md:px-sm 2xl:hidden' : 'md:hidden',
        )}
        onClick={onToggleMobile}
        aria-label={wide ? t('app.menu') : t('app.toggleNav')}
        aria-expanded={mobileOpen}
        aria-controls="app-sidebar"
      >
        {mobileOpen ? <IconClose /> : <IconMenu />}
        {wide && (
          <span aria-hidden className="hidden text-small font-medium md:inline">
            {t('app.menu')}
          </span>
        )}
      </button>
      <div className="flex-1">
        <GlobalSearch />
      </div>
      {/* Language toggle — Canvas Sand pill, announces switch (DESIGN §5 Navigation). */}
      <button
        type="button"
        onClick={() => chooseLocale(locale === 'en' ? 'ar' : 'en')}
        aria-live="polite"
        className="inline-flex min-h-[44px] items-center rounded-pill border-[0.5px] border-hairline bg-canvas-sand px-[10px] py-[5px] md:min-h-0 font-mono text-label uppercase tracking-[0.1em] text-ink-deep hover:border-accent-blue focus-visible:shadow-focus"
      >
        {locale === 'en' ? 'عربي' : 'EN'}
      </button>
      <AccountMenu />
    </header>
  );
}
